/**
 * The customer details we ask for, as configured in Internal > Tools > Customer
 * Data Configuration. Mirrors functions/src/customer-fields.ts; the server
 * sanitizes what it stores and re-checks every mandatory field on save, so this
 * copy only decides what the buyer sees.
 */
import { Address } from "./company";

export const CUSTOMER_FIELD_IDS = [
  "email",
  "name",
  "companyName",
  "address",
  "mainAddress",
  "website",
  "telephone",
  "cellphone",
  "fax",
] as const;

export type CustomerFieldId = (typeof CUSTOMER_FIELD_IDS)[number];

export type CustomerFieldConfig = {
  id: CustomerFieldId;
  requested: boolean;
  mandatory: boolean;
};

export type CustomerFieldsConfig = { fields: CustomerFieldConfig[] };

export type CustomerProfile = {
  email?: string;
  name?: string;
  companyName?: string;
  address?: Address | null;
  mainAddress?: Address | null;
  website?: string;
  telephone?: string;
  cellphone?: string;
  fax?: string;
};

/**
 * What sign-up asked for before the configuration existed. Used when the config
 * cannot be fetched, so a network problem on that one call does not leave a new
 * customer with a form that asks for nothing.
 */
export const FALLBACK_CUSTOMER_FIELDS: CustomerFieldsConfig = {
  fields: [
    { id: "email", requested: true, mandatory: true },
    { id: "name", requested: true, mandatory: false },
    { id: "companyName", requested: true, mandatory: false },
    { id: "address", requested: true, mandatory: true },
    { id: "mainAddress", requested: false, mandatory: false },
    { id: "website", requested: false, mandatory: false },
    { id: "telephone", requested: false, mandatory: false },
    { id: "cellphone", requested: false, mandatory: false },
    { id: "fax", requested: false, mandatory: false },
  ],
};

export const fieldConfig = (
  config: CustomerFieldsConfig,
  id: CustomerFieldId,
): CustomerFieldConfig =>
  config.fields.find((field) => field.id === id) || {
    id,
    requested: false,
    mandatory: false,
  };

export const asks = (config: CustomerFieldsConfig, id: CustomerFieldId) =>
  fieldConfig(config, id).requested;

export const requires = (config: CustomerFieldsConfig, id: CustomerFieldId) => {
  const field = fieldConfig(config, id);
  return field.requested && field.mandatory;
};

export const CUSTOMER_FIELD_LABELS: Record<CustomerFieldId, string> = {
  email: "Email address",
  name: "Your name",
  companyName: "Company name",
  address: "Company address",
  mainAddress: "Main address (headquarters)",
  website: "Website",
  telephone: "Telephone",
  cellphone: "Cellphone",
  fax: "Fax",
};

export const emptyAddress = (): Address => ({
  streetLines: [],
  city: "",
  postalCode: "",
  stateOrProvinceCode: "",
  countryCode: "",
});
